// RiskAssessmentForm:
// - determines which risk factors may be selected on page load
// - maintains its own store of selected risk factors
// - emits risk factor selection data for other UI updates


import {
  store,
} from 'reefjs'

import {
  debounce,
  isValidAssessmentItemId,
  setupLogger,
} from './utils.js'

const { logger, loggerError } = setupLogger('RiskAssessmentForm', 'turquoise')


export default class RiskAssessmentForm {
  constructor(formElement) {
    logger(this)

    this.formEl = formElement
    this.assessmentId = this.formEl.getAttribute('data-assessment-form')
    this.changeEvent = new Event('change', { bubbles: true })

    const duplicateForms = Array.from(document.querySelectorAll(`[data-assessment-form="${this.assessmentId}"]`))
      .filter((element) => element !== this.formEl)

    try {
      if (duplicateForms.length > 0) {
        throw new Error('Found more than one form element with the same assessment ID')
      }
    } catch(error) {
      loggerError(error, `Duplicate form elements with assessment ID ${this.assessmentId}:`, duplicateForms)
    }

    this.selectionStore = store(new Set(), {
      add(store, selectionId) {
        logger('adding to store:', selectionId)
        store.add(selectionId)
        Storage.set(selectionId, true)
      },
      delete(store, selectionId) {
        logger('deleting from store:', selectionId)
        store.delete(selectionId)
        Storage.remove(selectionId)
      },
    }, 'risk-selection')


    document.addEventListener('DOMContentLoaded', () => {
      logger(this.formEl.elements)

      this.updateFromStorage()

      // Handle all fields that are already checked but may not have been saved to localStorage:
      Array.from(this.formEl.elements).forEach((element) => {
        if (this.isValidSelection(element) && element.checked) {
          this.handleSelection(element, true)
        }
      })

      // Listen for further selections:
      this.formEl.addEventListener('change', (event) => {
        if (!this.isValidSelection(event.target)) return false
        this.handleSelection(event.target, event.target.checked)
      })

      // And to changes to the case ID:
      const caseIdField = this.formEl.querySelector('#case_id')
      if (caseIdField) {
        caseIdField.addEventListener('input', debounce((event) => {
          if (event.target.value) {
            Storage.set('case_id', event.target.value)
          }
        }, 50))
      }

      // Listen for loading of new case data:
      document.addEventListener('case-manager:loaded', (event) => {
        logger('heard loaded event')
        this.updateFromStorage()

        Array.from(this.formEl.elements).forEach((element) => {
          if (this.isValidSelection(element) && element.checked) {
            this.handleSelection(element, true)
          }
        })
      })

      // Listen for deletion of case data:
      document.addEventListener('case-manager:deleted', (event) => {
        logger('heard deleted event')

        Array.from(this.formEl.elements).forEach((el) => {
          if (el.checked) {
            el.checked = false
            el.dispatchEvent(this.changeEvent)
          }
        })
        this.formEl.reset()
      })

    })
  }

  isCaseId(element) {
    return element.type === 'text' && element.id === 'case_id'
  }

  isValidSelection(element) {
    // If an item does not appear in our records... it does not exist!!!

    const isCorrectType = element.type === 'checkbox' || element.type === 'radio'
    const isValidId = isValidAssessmentItemId(element.id, this.assessmentId)
    // Allow "none of the above" selections for radio groups:
    const isNoneSelection = element.type === 'radio' && element.value === 'none'

    return (isCorrectType && isValidId) || isNoneSelection
  }

  updateFromStorage() {
    const cachedSelection = Object.keys(localStorage)

    // Update UI based on selections saved in localStorage:
    Array.from(this.formEl.elements).forEach((field) => {
      if (field.role === 'radiogroup') {
        const radioGroupName = field.querySelector('input[type="radio"]').name
        const radioNodeList = this.formEl.elements[radioGroupName]
        const radioSavedSelection = Array.from(radioNodeList).map(i => i.id).filter(i => cachedSelection.includes(i))

        // This if/else statement deliberately does not handle cases where radioSavedSelection.length === 0.
        // If that's the case, and there is a radio button checked on page load, it will get handled next.

        if (radioSavedSelection.length > 1) {
          this.handleDuplicateRadioSelections(field, radioNodeList, radioSavedSelection)

        } else if (radioSavedSelection.length === 1) {
          const radioChecked = Array.from(radioNodeList).filter(i => i.checked)

          if (radioChecked.length === 1 && radioChecked[0].id !== radioSavedSelection[0]) {
            // A different radio button is checked than was saved to localStorage.
            // Retain the UI's selection rather than overriding it with an outdated selection:
            this.selectionStore.delete(radioSavedSelection[0])
            this.selectionStore.add(radioChecked[0].id)
          } else {
            Array.from(radioNodeList).forEach(i => i.checked = i.id === radioSavedSelection[0])
          }
        }

      } else if (field.type === 'checkbox' && this.isValidSelection(field) && cachedSelection.includes(field.id)) {
        field.checked = true

      } else if (this.isCaseId(field) && cachedSelection.includes(field.id)) {
        field.value = Storage.get('case_id')
      }
    })
  }

  handleSelection(inputEl, isSelected) {
    const selectionId = inputEl.id

    if (inputEl.type === 'radio') {
      this.handleRadioSelection(inputEl.name, selectionId, isSelected)
    } else {
      if (isSelected) {
        this.selectionStore.add(selectionId)
      } else {
        this.selectionStore.delete(selectionId)
      }
    }
  }

  handleRadioSelection(radioGroupName, selectionId, isSelected) {
    const radioNodeList = this.formEl.elements[radioGroupName]
    Array.from(radioNodeList).forEach((option) => {
      if (option.id === selectionId && isSelected && option.value !== 'none') {
        this.selectionStore.add(option.id)
      } else {
        this.selectionStore.delete(option.id)
      }
    })
  }

  handleDuplicateRadioSelections(field, radioNodeList, selectedIds) {
    const radioGroupName = radioNodeList.item(0).name
    const problemFields = selectedIds.map((i) => Array.from(radioNodeList).filter((input) => input.id === i)[0])
    const problemFieldsReport = problemFields.map((f) => {
      return `\nLabel: "${f.labels[0].innerText}"\nID: ${f.id}`
    }).join('\n')

    loggerError(
      `Found multiple selections saved from the radio button group ${radioGroupName} ("${field.querySelector('legend').innerText}"), but only one selection in this group is permitted.
      \nDeleting conflicting selections:\n`,
      problemFieldsReport
    )

    problemFields.forEach((f) => f.checked = false)
    selectedIds.forEach((i) => Storage.remove(i))
  }
}


